import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import { CATEGORIES } from "@/lib/categories";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";

const TIERS = [
  { key: "basic", label: "Basic", price: 25, blurb: "Smaller rotation share. Great for short-burst awareness." },
  { key: "standard", label: "Standard", price: 75, blurb: "3× impressions. Balanced reach for most listings." },
  { key: "premium", label: "Premium", price: 200, blurb: "Top placement + Sponsored badge on your listing." },
];

const PLACEMENTS = [
  { key: "home", label: "Home page", help: "Above the directory categories on the homepage." },
  { key: "sidebar", label: "Sidebar", help: "Sidebar slot across directory and listing pages." },
  { key: "category", label: "Category", help: "Targeted to one specialty category page." },
];

const today = () => new Date().toISOString().slice(0, 10);
const plus = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);

export default function NewAdPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    image_url: "",
    headline: "",
    link_url: "",
    placement: "home",
    category_filter: "",
    start_at: today(),
    end_at: plus(30),
    budget_tier: "standard",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validStep1 = form.image_url && form.headline.length >= 2 && form.link_url.startsWith("https://") &&
    (form.placement !== "category" || !!form.category_filter);
  const validStep2 = !!form.start_at && !!form.end_at && form.start_at < form.end_at;

  const submit = async () => {
    setSubmitting(true);
    try {
      const created = await api.post("/ads", form);
      const adId = created.data.id;
      const checkout = await api.post(`/ads/${adId}/checkout`, { origin_url: window.location.origin });
      window.location.href = checkout.data.checkout_url;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) {
        toast.error("Stripe is not configured yet. Ask the platform admin to set Stripe keys.");
      } else {
        toast.error(e?.response?.data?.detail || "Could not create ad");
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="container-page py-12" data-testid="new-ad-page">
      <Link to="/dashboard/ads" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent">
        <ArrowLeft className="h-4 w-4" /> Back to my ads
      </Link>
      <h1 className="mt-6 font-display text-4xl font-bold text-primary">Create an ad</h1>
      <ol className="mt-4 flex flex-wrap gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        {["Creative", "Schedule", "Budget"].map((label, i) => (
          <li key={label} className={`rounded-full px-3 py-1 font-semibold ${step === i + 1 ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
            {i + 1}. {label}
          </li>
        ))}
      </ol>

      {step === 1 && (
        <div className="card-surface mt-6 space-y-5 p-6" data-testid="ad-step-1">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ad image (16:10 recommended)</label>
            <ImageUpload onUploaded={(v) => set("image_url", v)} testId="ad-image-upload" />
            {form.image_url && (
              <p className="mt-2 text-xs text-success" data-testid="ad-image-uploaded">
                Image uploaded ✓
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Headline (max 60)</label>
            <input
              maxLength={60}
              value={form.headline}
              onChange={(e) => set("headline", e.target.value)}
              className="mt-2 w-full rounded-md border border-border bg-card px-4 py-2 text-foreground"
              placeholder="Trusted dental care in central Denver"
              data-testid="ad-headline"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Destination URL (https only)</label>
            <input
              type="url"
              value={form.link_url}
              onChange={(e) => set("link_url", e.target.value)}
              className="mt-2 w-full rounded-md border border-border bg-card px-4 py-2 text-foreground"
              placeholder="https://your-site.com/landing"
              data-testid="ad-link"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Placement</label>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              {PLACEMENTS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => set("placement", p.key)}
                  className={`rounded-md border p-3 text-left text-sm transition-colors ${form.placement === p.key ? "border-primary bg-primary/5" : "border-border"}`}
                  data-testid={`placement-${p.key}`}
                >
                  <div className="font-semibold text-primary">{p.label}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{p.help}</div>
                </button>
              ))}
            </div>
          </div>
          {form.placement === "category" && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Category</label>
              <select
                value={form.category_filter}
                onChange={(e) => set("category_filter", e.target.value)}
                className="mt-2 w-full rounded-md border border-border bg-card px-4 py-2 text-foreground"
                data-testid="ad-category"
              >
                <option value="">Select…</option>
                {CATEGORIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div className="flex justify-end">
            <button onClick={() => setStep(2)} disabled={!validStep1} className="btn-primary disabled:opacity-50" data-testid="step1-next">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="card-surface mt-6 space-y-5 p-6" data-testid="ad-step-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Start</label>
              <input
                type="date"
                value={form.start_at}
                onChange={(e) => set("start_at", e.target.value)}
                className="mt-2 w-full rounded-md border border-border bg-card px-4 py-2"
                data-testid="ad-start"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">End (max 90 days out)</label>
              <input
                type="date"
                value={form.end_at}
                max={plus(90)}
                onChange={(e) => set("end_at", e.target.value)}
                className="mt-2 w-full rounded-md border border-border bg-card px-4 py-2"
                data-testid="ad-end"
              />
            </div>
          </div>
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="btn-secondary"><ArrowLeft className="h-4 w-4" /> Back</button>
            <button onClick={() => setStep(3)} disabled={!validStep2} className="btn-primary disabled:opacity-50" data-testid="step2-next">Continue <ArrowRight className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="card-surface mt-6 space-y-5 p-6" data-testid="ad-step-3">
          <div className="grid gap-4 sm:grid-cols-3">
            {TIERS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => set("budget_tier", t.key)}
                className={`rounded-xl border-2 p-5 text-left transition-all ${form.budget_tier === t.key ? "border-accent bg-accent/5" : "border-border"}`}
                data-testid={`tier-${t.key}`}
              >
                <div className="font-display text-2xl font-bold text-primary">{t.label}</div>
                <div className="mt-1 font-display text-3xl font-bold text-accent-dark">${t.price}</div>
                <p className="mt-3 text-sm text-muted-foreground">{t.blurb}</p>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            You'll be redirected to Stripe Checkout. Test card: 4242 4242 4242 4242 · any future date · any CVC · any ZIP.
          </p>
          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="btn-secondary"><ArrowLeft className="h-4 w-4" /> Back</button>
            <button onClick={submit} disabled={submitting} className="btn-accent disabled:opacity-50" data-testid="ad-submit">
              {submitting ? "Redirecting…" : `Pay $${TIERS.find((t) => t.key === form.budget_tier).price} & launch`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
